/**
 * The full-window layout, for the screens that are an editor rather than a page.
 *
 * Salesforge opens a sequence's builder and its import wizards as takeovers:
 * the sidebar goes, the screen is the whole window, a 68px header carries the
 * thing's name, its state, the one commit button and a close X, and a tab strip
 * may sit under it. The honest shape of that is a route outside the shell
 * rather than a portal over it, and this is that route's layout. `routes.tsx` registers it beside `AppShell` under
 * `RequireAuth`, with no route in it yet: the builder and the wizards move in
 * when their own work lands, and `Takeover.tsx` is deleted when its last user
 * has moved.
 *
 * WHAT IT KEEPS FROM THE SHELL. The brand's colour across the top, because a
 * builder is exactly where somebody is about to send from a brand and must not
 * have to read a label to know which one. The health strip, because a dry run
 * matters most on the screen that launches things. The skip link, the error
 * boundary and the brand-in-address rule. What it drops is everything that
 * navigates: the sidebar, the palette and the shell's keys. The X and Escape
 * leave, and nothing else does.
 *
 * NOTHING HERE IS STICKY. The header and the strip are the first rows of a
 * column that is exactly the window, and the body under them is the only thing
 * that scrolls, so there is nothing for a sticky header to stick against.
 */
import { Suspense, useCallback, useEffect, type ReactNode } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useBrandInAddressInvalidation } from '../components/AppShell'
import { Button } from '../components/Button'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { Spinner } from '../components/Spinner'
import { LoadingState } from '../components/States'
import { SystemBanner } from '../components/SystemBanner'
import { IconCheck, IconClose } from '../components/icons'
import { cn } from '../lib/cn'

// ---------------------------------------------------------------------------
// Leaving
// ---------------------------------------------------------------------------

/**
 * Where the X goes: back to the list somebody came from, or the list route.
 *
 * The opener passes `{ from: location.pathname + location.search }` in the
 * navigation state, so closing lands on the same filtered page rather than on
 * page one. Only a same-origin path is taken. A value starting `//` is a
 * protocol-relative URL that a browser resolves to another site, so "starts
 * with a slash" is not enough on its own.
 */
export function takeoverReturnPath(state: unknown, fallback: string): string {
  if (typeof state !== 'object' || state === null) return fallback
  const from = (state as { from?: unknown }).from
  if (typeof from !== 'string') return fallback
  if (!from.startsWith('/') || from.startsWith('//') || from.startsWith('/\\')) return fallback
  return from
}

/** Leave the takeover for wherever `takeoverReturnPath` says. */
export function useTakeoverClose(fallback: string): () => void {
  const location = useLocation()
  const navigate = useNavigate()
  return useCallback(
    () => navigate(takeoverReturnPath(location.state, fallback)),
    [location.state, fallback, navigate],
  )
}

/**
 * Whether Escape belongs to the takeover right now.
 *
 * Not while an overlay is open, a dialog, a drawer, a menu or a list, because
 * Escape closes that first. Not when another listener has already taken the
 * press. Not while somebody is in a field either, where Escape means "stop
 * editing this" and throwing the whole screen away would be the most
 * destructive reading of the key.
 */
export function escapeLeaves(
  event: Pick<KeyboardEvent, 'key' | 'defaultPrevented' | 'target'>,
  doc: Pick<Document, 'querySelector'>,
): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) return false
  if (doc.querySelector('[aria-modal="true"], [role="dialog"], [role="menu"], [role="listbox"]') !== null) return false
  // Read by shape rather than `instanceof HTMLElement`, so the rule can be
  // covered in a test run that has no DOM.
  const target = event.target as { tagName?: unknown; isContentEditable?: unknown } | null
  const tag = typeof target?.tagName === 'string' ? target.tagName : ''
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable === true) return false
  return true
}

/**
 * The Escape listener, deciding a tick after the press.
 *
 * Drawers, dialogs, the canvas and its panels all close on Escape, and some
 * of them listen on the window after this one started listening, so deciding
 * at once would leave the screen on the same press that closed the thing on
 * top. On the next tick every listener has run: a press another one took is
 * marked as taken, and an overlay that stays open is still in the document.
 * `schedule` is the timer, passed in so the rule can be covered without one.
 */
export function settledEscapeHandler(
  leave: () => void,
  doc: Pick<Document, 'querySelector'>,
  schedule: (run: () => void) => void,
): (event: Pick<KeyboardEvent, 'key' | 'defaultPrevented' | 'target'>) => void {
  return (event) => {
    if (event.key !== 'Escape') return
    schedule(() => {
      if (escapeLeaves(event, doc)) leave()
    })
  }
}

/** Binds Escape to leaving, on the terms `escapeLeaves` sets, once every other listener has had the key. */
export function useLeaveOnEscape(leave: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return undefined
    const timers = new Set<number>()
    const onKey = settledEscapeHandler(leave, document, (run) => {
      const id = window.setTimeout(() => {
        timers.delete(id)
        run()
      }, 0)
      timers.add(id)
    })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      for (const id of timers) window.clearTimeout(id)
    }
  }, [leave, enabled])
}

// ---------------------------------------------------------------------------
// The pieces a takeover page draws
// ---------------------------------------------------------------------------

export type AutosaveState = 'idle' | 'saving' | 'saved'

/**
 * Saving, then Saved, in place. Nothing before the first change, because a chip
 * saying Saved over a draft nobody has touched claims work that did not happen.
 * After that it stays, so the last word on screen is always the true one.
 */
export function AutosaveChip({ state }: { state: AutosaveState }) {
  if (state === 'idle') return null
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-1.5 text-base font-medium text-ink-muted">
      {state === 'saving' ? (
        <>
          <Spinner size="sm" label={null} className="text-ink-subtle" />
          Saving
        </>
      ) : (
        <>
          <IconCheck className="h-4 w-4 animate-check-in text-success-text" />
          Saved
        </>
      )}
    </span>
  )
}

/** The close X every takeover header ends with. */
export function TakeoverCloseButton({ onClose, label = 'Close' }: { onClose: () => void; label?: string }) {
  return (
    <Button variant="ghost" size="md" iconOnly aria-label={label} title={label} onClick={onClose}>
      <IconClose className="h-5 w-5" />
    </Button>
  )
}

export interface TakeoverHeaderProps {
  /** The thing's name, usually the dashed name field the page owns. */
  name: ReactNode
  /** A status badge after the name, such as Draft or Running. */
  status?: ReactNode
  /** The autosave chip's state. Leave it out on a screen that does not autosave. */
  autosave?: AutosaveState
  /**
   * The commit button, and AT MOST ONE OF THEM IS `variant="primary"`, last,
   * the same rule the page header keeps.
   */
  actions?: ReactNode
  /** The list this screen belongs to, for when nobody passed a way back. */
  closeTo: string
  closeLabel?: string
  /** The strip under the header, drawn at its own 38px with its own rule. */
  tabs?: ReactNode
  /** Leave on Escape when nothing else wants the key. On by default. */
  escapeLeaves?: boolean
  className?: string
}

/**
 * The 68px band across the top of a takeover: name, state and autosave on the
 * left, the commit and the X on the right, the strip under it.
 */
export function TakeoverHeader({
  name,
  status,
  autosave,
  actions,
  closeTo,
  closeLabel,
  tabs,
  escapeLeaves: leavesOnEscape = true,
  className,
}: TakeoverHeaderProps) {
  const close = useTakeoverClose(closeTo)
  useLeaveOnEscape(close, leavesOnEscape)

  return (
    <div className={cn('shrink-0 border-b border-line bg-canvas', className)}>
      <header className="flex h-header items-center gap-3 px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="min-w-0">{name}</div>
          {status}
          {autosave === undefined ? null : <AutosaveChip state={autosave} />}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
        <TakeoverCloseButton onClose={close} label={closeLabel} />
      </header>
      {tabs ? <div className="h-tabstrip px-6">{tabs}</div> : null}
    </div>
  )
}

/** What is under the header, and the only thing in a takeover that scrolls. */
export function TakeoverBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto', className)}>{children}</div>
}

// ---------------------------------------------------------------------------
// The layout route
// ---------------------------------------------------------------------------

export function TakeoverLayout() {
  const location = useLocation()
  useBrandInAddressInvalidation(location.search)

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <SystemBanner />
      <main id="main" className="flex min-h-0 flex-1 flex-col">
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<LoadingState label="Opening the page" />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  )
}
